// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { Dropdown, IDropdownOption, ITextFieldStyles, Label, Stack, TextField } from '@fluentui/react';
import { screenshottingSupported } from 'feedbacks/TakeScreenshot';
import { FeedbackScreenshot, FeedbackScreenshotProps } from './FeedbackScreenshot';

export const FeedbackDialogBody = (
  props: {
    guid: string | undefined;
    defaultFeedbackType: string;
    comment: string | undefined;
    title: string | undefined;
    setTitle: (title: string | undefined) => void;
    setComment: (comment: string | undefined) => void;
    setFeedbackType: (feedbackType: string) => void;
  } & FeedbackScreenshotProps
): JSX.Element => {
  return (
    <>
      {!props.guid && (
        <FeedbackInput
          defaultFeedbackType={props.defaultFeedbackType}
          comment={props.comment}
          title={props.title}
          setTitle={props.setTitle}
          setComment={props.setComment}
          setFeedbackType={props.setFeedbackType}
          onScreenshotResultChanged={props.onScreenshotResultChanged}
          onBeforeScreenshot={props.onBeforeScreenshot}
          onAfterScreenshot={props.onAfterScreenshot}
          onScreenshotFailed={props.onScreenshotFailed}
        />
      )}
      {props.guid && <FeedbackSubmitted guid={props.guid} />}
    </>
  );
};

const FeedbackInput = (
  props: {
    defaultFeedbackType: string;
    comment: string | undefined;
    title: string | undefined;
    setTitle: (title?: string) => void;
    setFeedbackType: (feedbackType: string) => void;
    setComment: (comment?: string) => void;
  } & FeedbackScreenshotProps
): JSX.Element => (
  <Stack tokens={{ childrenGap: '1rem' }}>
    <TextField
      label="Title"
      placeholder="Enter a title for your feedback"
      onChange={(_, value): void => {
        props.setTitle(value);
      }}
      value={props.title}
      maxLength={1500}
      required
    />
    <Dropdown
      label="What is this related to?"
      defaultSelectedKey={props.defaultFeedbackType}
      options={options}
      onChange={(_, item): void => {
        props.setFeedbackType(item?.key as string);
      }}
      required
    />
    <TextField
      label="What are you seeing? Has it always been that way? Any step to repro?"
      value={props.comment}
      onChange={(_, value): void => {
        props.setComment(value);
      }}
      multiline
      styles={textFieldStyles}
    />
    {screenshottingSupported() && (
      <Stack.Item>
        <Label>Attach a screenshot (Optional)</Label>
        <FeedbackScreenshot
          onScreenshotResultChanged={props.onScreenshotResultChanged}
          onBeforeScreenshot={props.onBeforeScreenshot}
          onAfterScreenshot={props.onAfterScreenshot}
          onScreenshotFailed={props.onScreenshotFailed}
        />
      </Stack.Item>
    )}
  </Stack>
);

const FeedbackSubmitted = (props: { guid: string | undefined }): JSX.Element => (
  <>
    <Label>Your feedback has been submitted with below tracking id!</Label>
    <TextField value={props.guid} readOnly />
  </>
);

const options: IDropdownOption[] = [
  { key: 'Calling - Audio', text: 'Calling - Audio' },
  { key: 'Calling - Call drops', text: 'Calling - Call drops' },
  { key: 'Calling - Other', text: 'Calling - Other' },
  { key: 'Calling - Video', text: 'Calling - Video' },
  { key: 'Calling - Screen sharing', text: 'Calling - Screen sharing' },
  { key: 'Chat', text: 'Chat - General' },
  { key: 'Participants list', text: 'Participants list' },
  { key: 'Join experience', text: 'Join experience' },
  { key: 'Call Monitor', text: 'Call Monitor' },
  { key: 'Others', text: 'Others' }
];

const textFieldStyles: Partial<ITextFieldStyles> = {
  field: {
    minHeight: '13rem'
  }
};
